import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Heart, Share2 } from "lucide-react";
import { useNftInteractions } from "@/hooks/useNftInteractions";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface NftInteractionsBarProps {
  nftId: string;
  nftName: string;
  nftImageUrl?: string | null;
  ownerWallet: string;
  ownerName: string | null;
  priceUsd: number;
  priceEmon?: number;
  variant?: "compact" | "full";
  className?: string;
}

export function NftInteractionsBar({
  nftId,
  variant = "compact",
  className,
}: NftInteractionsBarProps) {
  const navigate = useNavigate();
  const {
    likes_count,
    user_has_liked,
    loading,
    toggleLike,
  } = useNftInteractions(nftId);

  // Cliccando condividi, naviga alla pagina NFT
  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/nft/${nftId}`);
  };

  if (variant === "compact") {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {/* Like button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "h-8 px-2 gap-1",
                  user_has_liked && "text-red-500 hover:text-red-600"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLike();
                }}
                disabled={loading}
              >
                <Heart
                  className={cn("w-4 h-4", user_has_liked && "fill-current")}
                />
                <span className="text-xs">{likes_count}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{user_has_liked ? "Remove like" : "Like this NFT"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Share button - naviga alla pagina NFT */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Share this NFT"
                className="h-8 px-2"
                onClick={handleShareClick}
              >
                <Share2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Share this NFT</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Flow badge */}
        <Badge variant="outline" className="text-xs bg-primary/10 border-primary/30 text-primary">
          Flow
        </Badge>
      </div>
    );
  }

  // Full variant for detail views
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Like */}
          <Button
            variant={user_has_liked ? "default" : "outline"}
            size="sm"
            className={cn("gap-2", user_has_liked && "bg-red-500 hover:bg-red-600")}
            onClick={toggleLike}
            disabled={loading}
          >
            <Heart className={cn("w-4 h-4", user_has_liked && "fill-current")} />
            {likes_count} {likes_count === 1 ? "Like" : "Likes"}
          </Button>
        </div>
      </div>

      {/* Flow badge */}
      <Badge variant="outline" className="bg-primary/10 border-primary/30 text-primary">
        ✨ Minted on Flow
      </Badge>
    </div>
  );
}
